<template>
    <div class="timeline-comment-input">
        <div class="grid-fitted grid-unstackable">
            <div v-if="isProcessing" class="grid-item no-flex inline-s position-relative">
                <div style="width: 36px;height: 36px;">
                    <v-inline-loader :secondary="true"/>
                </div>
            </div>
            <div v-else class="grid-item no-flex inline-s">
                <v-avatar :initials="initials"/>
            </div>
            <div class="grid-item">
                <div class="field-input" :class="{'is-focus': contentFocus}">
                    <div class="grid-fitted grid-unstackable">
                        <div class="grid-item" v-fs-block>
                            <div v-if="!contentFocus"
                                 :contenteditable="isInputContentEditable"
                                 class="content-editable"
                                 @focus="contentEditableFocus">
                                <span class="text-muted">
                                    <!-- todo user friendly text -->
                                    <span v-if="isProcessing">Please wait...</span>
                                    <span v-else>
                                        Write a new note and press enter
                                    </span>
                                </span>
                            </div>
                            <div v-else
                                 :contenteditable="isInputContentEditable"
                                 class="content-editable break-all exclude-global-search"
                                 @keydown="keyDown"
                                 @keyup="keyUp"
                                 ref="contentEditable"
                                 @blur="contentEditableBlur">
                            </div>
                        </div>
                        <div class="grid-item no-flex" :class="{'stack-xs': attachments.length}">
                            <v-icon-button
                                :disabled="isProcessing"
                                type="basic"
                                size="small"
                                @click="addTokenText('@')">
                                <v-icon icon="at"/>
                            </v-icon-button>
                            <v-file-selector
                                @input="addAttachments"
                                :multiple="true"
                                :disabled="isProcessing">
                                <v-icon-button
                                    type="basic"
                                    size="small"
                                    slot-scope="{openFileBrowser}"
                                    @click="openFileBrowser"
                                    :disabled="isProcessing">
                                    <v-icon icon="clip"/>
                                </v-icon-button>
                            </v-file-selector>
                            <v-icon-button
                                type="basic"
                                size="small"
                                @click="addTokenText('#')"
                                :disabled="isProcessing">
                                <v-icon icon="sharp"/>
                            </v-icon-button>
                        </div>
                    </div>
                    <div v-if="attachments.length" class="grid-fitted grid-unstackable">
                        <div class="grid-item">
                            <ul class="list list-group list-compact">
                                <li v-for="(attachment, index) in attachments" class="list-item align-items-center">
                                    <span class="text-muted no-flex inline-s">{{attachment.name}}</span>
                                    <v-icon-button
                                        type="basic"
                                        size="small"
                                        class="no-flex"
                                        @click="removeAttachment(index)"
                                        :disabled="isProcessing">
                                        <v-icon icon="trash"/>
                                    </v-icon-button>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <suggestion-picker
                    ref="suggestionPicker"
                    :providers="suggestionProviders"
                    @input="suggestionSelected"/>
            </div>
        </div>
    </div>
</template>

<script>
    import {
        vFileSelector,
        vIcon,
        vIconButton,
    } from '@/components/form';
    import typeaheadInput from '@/components/typeahead-input/typeahead-input';
    import SuggestionPicker from '@/components/typeahead-input/suggestion-picker';
    import vInlineLoader from '@/components/v-inline-loader';
    import vAvatar from '@/components/v-avatar';
    import CommentHelper from './comment-helper';

    export default {
        name: 'comment-input',
        extends: typeaheadInput,
        components: {
            vAvatar,
            vIconButton,
            vIcon,
            vFileSelector,
            SuggestionPicker,
            vInlineLoader,
        },
        props: {
            isProcessing: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                typedHTMLInput: null,
                attachments: [],
                contentFocus: false,
            };
        },
        computed: {
            isInputContentEditable() {
                return !this.isProcessing;
            },
            initials() {
                return this.$store.getters['user/initials'];
            },
        },
        methods: {
            keyDown(event) {
                if (this.$refs.suggestionPicker.isActionKey(event)) {
                    event.preventDefault();
                } else if (event.keyCode === 13 && !(event.shiftKey || event.ctrlKey || event.altKey)) {
                    event.preventDefault();
                    if (this.hasMoreThanSpaces(this.typedInput)) {
                        this.postComment();
                    }
                }
            },
            keyUp(event) {
                if (this.$refs.suggestionPicker.isActionKey(event)) {
                    this.$refs.suggestionPicker.keyUp(event);
                } else {
                    this.typedInput = CommentHelper.htmlToPainText(this.$refs.contentEditable);
                    this.$refs.suggestionPicker.checkForSuggestions(this.typedInput);
                }
            },
            suggestionSelected(updatedInput) {
                this.typedInput = updatedInput;
                this.$refs.contentEditable.innerHTML = CommentHelper.plainTextToEditableHTML(this.typedInput, this.suggestionProviders);
                this.setCursorToEnd(this.$refs.contentEditable);
            },
            /**
             * Set the coursor on the end of a element
             * @param {HTMLElement} element - Element to position the cursor
             * */
            setCursorToEnd(element) {
                const range = document.createRange();
                const sel = window.getSelection();
                range.setStart(element, element.childNodes.length);
                range.collapse(true);
                sel.removeAllRanges();
                sel.addRange(range);
            },
            postComment() {
                const message = {comment: this.typedInput};
                if (this.attachments.length) {
                    message.attachments = [...this.attachments];
                }
                this.$emit('input', message);
                this.clearInput();
            },
            addTokenText(token) {
                if (!this.contentFocus) {
                    this.contentEditableFocus();
                }
                const input = this.typedInput ? `${this.typedInput} ${token}` : token;
                this.$refs.suggestionPicker.checkForSuggestions(input);
            },
            addAttachments(fileList) {
                this.attachments = [...this.attachments, ...fileList];
            },
            removeAttachment(index) {
                this.attachments.splice(index, 1);
            },
            contentEditableFocus() {
                this.contentFocus = true;
                this.$nextTick(() => this.$refs.contentEditable.focus());
            },
            contentEditableBlur() {
                if (this.hasMoreThanSpaces(this.typedInput)
                    || this.$refs.suggestionPicker.isVisible) {
                    return;
                }
                this.contentFocus = false;
                this.clearInput();
            },
            hasMoreThanSpaces(stringValue) {
                return stringValue.replace(CommentHelper.spaceRegex, '').length > 0;
            },
            clearInput() {
                this.typedInput = '';
                this.$refs.contentEditable.innerHTML = '';
                this.attachments = [];
            },
        },
    };
</script>
